import {Anchor} from 'src/design/atoms/Anchor';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import type {DocumentReviewRoundResponse} from 'src/api/ProactApiClient';
import {DocumentReviewTypeEnum} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  DocumentReviewRoundStatePill,
  DocumentReviewTypeLabel,
} from 'src/pages/Modules/DocumentManagement/documentReviewHelpers';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {formatDate} from 'src/shared/utils/formatters';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface ReviewRoundDetailProps {
  rounds: DocumentReviewRoundResponse[];
}

/**
 * Review round history for a single document version — rendered in the
 * Versions tab expandable row detail.
 */
export const ReviewRoundDetail: React.FC<ReviewRoundDetailProps> = ({
  rounds,
}) => {
  const navigate = useAppNavigate();

  if (rounds.length === 0) {
    return (
      <Text size="sm" c="dimmed" p="md" data-testid="review-round-detail-empty">
        No review rounds for this version.
      </Text>
    );
  }

  return (
    <Stack gap="sm" p="md" data-testid="review-round-detail">
      {rounds.map(round => {
        const statePill = DocumentReviewRoundStatePill(round.state);

        return (
          <Stack key={round.id} gap={2}>
            <Group gap="xs">
              <Text fw={500} size="sm">
                {DocumentReviewTypeLabel(round.reviewType)}
              </Text>
              <Pill {...statePill} />
              {round.taskDefinitionId && (
                <Anchor
                  size="sm"
                  onClick={() =>
                    navigate(
                      PathUtility.TaskView(
                        round.taskType,
                        round.taskDefinitionId!,
                        'content'
                      )
                    )
                  }>
                  View task
                </Anchor>
              )}
            </Group>
            {round.resolvedByName && round.resolvedAt && (
              <Text size="xs" c="dimmed">
                Decided by {round.resolvedByName} on{' '}
                {formatDate(round.resolvedAt)}
              </Text>
            )}
            {round.resolutionComment && (
              <Text size="sm">
                &ldquo;{round.resolutionComment}&rdquo;
              </Text>
            )}
            {round.reviewType === DocumentReviewTypeEnum.Acknowledgment &&
              round.participationStatistics && (
                <Text size="xs" c="dimmed">
                  Acknowledged {round.participationStatistics.completed} /{' '}
                  {round.participationStatistics.total}
                </Text>
              )}
          </Stack>
        );
      })}
    </Stack>
  );
};
